import { ToastrService } from 'ngx-toastr';
import pluralize from 'pluralize';
import { Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';

@Component({
  selector: 'fs-appliance-network-reload',
  templateUrl: './appliance-network-reload.html',
  styleUrls: ['./appliance-network-reload.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApplianceNetworkReloadComponent implements OnChanges, OnDestroy {
  @Input() public networkSpeed: string = '—';
  @Input() public networkSpeedUpdatedAtToolTip: string = 'Last check: —';
  @Input() public networkSpeedTimestamp: Date;
  @Input() public isApplianceDown: boolean = false;
  @Output() public reload = new EventEmitter<{
    onFailure: () => void;
  }>();

  private readonly RELOAD_COOLDOWN_MS = 30 * 60 * 1_000; // 30 minutes
  private readonly MAX_LOADING_TIME_MS = 5 * 60 * 1_000; // 5 minutes

  private readonly MS_PER_SECOND = 1_000;
  private readonly MS_PER_MINUTE = 60 * 1_000;
  private readonly SECONDS_PER_MINUTE = 60;
  private readonly MINUTES_PER_HOUR = 60;
  private readonly HOURS_PER_DAY = 24;
  private readonly TIME_UNITS = {
    day: 'day',
    hour: 'hour',
    minute: 'minute',
    plural: 's',
  };

  public isLoading = false;

  private _nextReloadAllowedTime = 0;
  private readonly _destroy$ = new Subject<void>();

  constructor(private _cd: ChangeDetectorRef, private _toastr: ToastrService) {}

  get reloadButtonToolTip() {
    return this.isApplianceDown
      ? 'You cannot test network speed when your core status is down.'
      : !this.isLoading && Date.now() < this._nextReloadAllowedTime
      ? 'You have to wait at least ' +
        this._formatDuration(this.RELOAD_COOLDOWN_MS) +
        ' between each speed test.'
      : !this.isLoading
      ? 'Refresh upload speed.'
      : '';
  }

  ngOnChanges(changes: SimpleChanges) {
    // date timestamp change detection getting triggered due to parent polling for capacity-metrics.
    // So comparing the current and previous value of the date timestamp to check if it is a new value.
    if (
      changes?.networkSpeedTimestamp?.currentValue?.getTime() !=
      changes?.networkSpeedTimestamp?.previousValue?.getTime()
    ) {
      if (this.isLoading) {
        this._stopLoading();
      }
      // update the next reload allowed time if the network speed timestamp is more recent
      if (
        changes.networkSpeedTimestamp.currentValue?.getTime() + this.RELOAD_COOLDOWN_MS >
        this._nextReloadAllowedTime
      ) {
        this._nextReloadAllowedTime =
          changes.networkSpeedTimestamp.currentValue?.getTime() + this.RELOAD_COOLDOWN_MS;
        this._cd.markForCheck();
      }
    }
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public handleReload(): void {
    if (this.isReloadDisabled) {
      return;
    }
    this.reload.next({
      onFailure: () => {
        this._stopLoading();
      },
    });
    this._startLoading();
    this._nextReloadAllowedTime = Date.now() + this.RELOAD_COOLDOWN_MS; // 30 minutes throttle
  }

  get isReloadDisabled(): boolean {
    return this.isApplianceDown || Date.now() < this._nextReloadAllowedTime;
  }

  private _startLoading(): void {
    this.isLoading = true;

    timer(this.MAX_LOADING_TIME_MS)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this._onLoadingTimeout());

    timer(this.RELOAD_COOLDOWN_MS)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this._cd.markForCheck());
  }

  private _onLoadingTimeout() {
    this._toastr.error(
      'Refresh failed, try again in ' +
        this._formatDuration(this._nextReloadAllowedTime - Date.now()),
    );
    this._stopLoading();
  }

  private _stopLoading(): void {
    this.isLoading = false;
    this._cd.markForCheck();
  }

  private _formatDuration(milliseconds: number): string {
    if (milliseconds < this.MS_PER_MINUTE) {
      return 'less than a minute';
    }

    const seconds = Math.floor(milliseconds / this.MS_PER_SECOND);
    const minutes = Math.floor(seconds / this.SECONDS_PER_MINUTE);
    const hours = Math.floor(minutes / this.MINUTES_PER_HOUR);
    const days = Math.floor(hours / this.HOURS_PER_DAY);

    if (days > 0) {
      return `${days} ${pluralize(this.TIME_UNITS.day, days)}`;
    } else if (hours > 0) {
      return `${hours} ${pluralize(this.TIME_UNITS.hour, hours)}`;
    } else if (minutes > 0) {
      return `${minutes} ${pluralize(this.TIME_UNITS.minute, minutes)}`;
    }
    return '';
  }
}
